import { getIndexerGrpcWeb3GwApi } from '@/app/Services'
import type { FeeGrant, FeeGrantResponse } from '@/store/wallet/helpers/types'

const FEE_GRANT_EXPIRY_BUFFER_MS = 60_000
export const FEE_GRANT_REQUEST_TIMEOUT_MS = 30_000

export const feeGrantRequests: Record<string, undefined | Promise<FeeGrant>> =
  {}

export function getFeeGrantKey({
  granterAddress,
  granteeAddress
}: {
  granterAddress: string
  granteeAddress: string
}) {
  return `${granterAddress}:${granteeAddress}`
}

export function getFeeGrantRequestKey({
  granteeAddress,
  granterAddress
}: {
  granteeAddress: string
  granterAddress?: string
}) {
  return granterAddress
    ? getFeeGrantKey({ granterAddress, granteeAddress })
    : granteeAddress
}

export function parseFeeGrantResponse(params: {
  granteeAddress: string
  response: FeeGrantResponse
}): FeeGrant {
  const { response } = params
  const { spendLimit } = response
  const granterAddress = response.granterAddress

  if (!granterAddress) {
    throw new Error('Feegrant response missing granter')
  }

  const normalizedSpendLimit =
    spendLimit?.denom && spendLimit.amount !== undefined
      ? {
          denom: spendLimit.denom,
          amount: String(spendLimit.amount)
        }
      : undefined
  const allowedMessages = Array.isArray(response.allowedMessages)
    ? response.allowedMessages.filter(
        (message): message is string => typeof message === 'string'
      )
    : undefined

  return {
    granterAddress,
    allowedMessages,
    requestedAt: Date.now(),
    spendLimit: normalizedSpendLimit,
    txHash: response.txHash || undefined,
    expiresAt: getFeeGrantExpiration(response.expiration),
    granteeAddress: response.granteeAddress || params.granteeAddress
  }
}

export function getFeeGrantExpiration(
  expiration: FeeGrantResponse['expiration']
) {
  if (expiration === undefined || expiration === '') {
    return
  }

  const timestamp = Number(expiration)

  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return
  }

  return timestamp
}

export function isFeeGrantFresh(feeGrant: FeeGrant | undefined) {
  if (!feeGrant) {
    return false
  }

  if (!feeGrant.expiresAt) {
    return true
  }

  return feeGrant.expiresAt > Date.now() + FEE_GRANT_EXPIRY_BUFFER_MS
}

function withFeeGrantRequestTimeout<T>(request: Promise<T>) {
  let timeoutId: undefined | ReturnType<typeof setTimeout>
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(
          `Fee grant request timed out after ${FEE_GRANT_REQUEST_TIMEOUT_MS}ms`
        )
      )
    }, FEE_GRANT_REQUEST_TIMEOUT_MS)
  })

  return Promise.race([request, timeout]).finally(() => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  })
}

export async function prepareFeeGrant(params: { granteeAddress: string }) {
  const web3GatewayApi = await getIndexerGrpcWeb3GwApi()
  const response = await withFeeGrantRequestTimeout(
    web3GatewayApi.prepareFeeGrantRequest({
      granteeAddress: params.granteeAddress
    })
  )

  return parseFeeGrantResponse({
    response,
    granteeAddress: params.granteeAddress
  })
}
